/**
 * USER ROLES HOOK
 * 
 * React hook for working with the new secure role system.
 * Replaces direct checks of profile.is_admin, profile.is_partner, etc.
 * 
 * USAGE:
 * const { hasRole, isAdmin, isPremium, roles, loading } = useUserRoles();
 * 
 * if (isAdmin) {
 *   // Show admin UI
 * }
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

// ============================================================================
// TYPES
// ============================================================================

export type AppRole = 'admin' | 'partner' | 'premium' | 'moderator' | 'user';

export interface UserRole {
  id: string;
  user_id: string;
  role: AppRole;
  granted_at: string;
  granted_by: string | null;
  expires_at: string | null;
  created_at: string;
}

// ============================================================================
// MAIN HOOK
// ============================================================================

export function useUserRoles(userId?: string) {
  const { user } = useAuth();
  const targetUserId = userId || user?.id;

  const {
    data: roles = [],
    isLoading: loading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['user-roles', targetUserId],
    queryFn: async () => {
      if (!targetUserId) {
        return [];
      }

      const { data, error } = await supabase
        .from('user_roles')
        .select('*')
        .eq('user_id', targetUserId)
        .or('expires_at.is.null,expires_at.gt.now()'); // Only active roles

      if (error) {
        console.error('Error fetching user roles:', error);
        throw error;
      }

      return (data || []) as UserRole[];
    },
    enabled: !!targetUserId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  /**
   * Check if user has a specific role
   */
  const hasRole = (role: AppRole): boolean => {
    return roles.some(r => r.role === role);
  };

  /**
   * Check if user has any of the specified roles
   */
  const hasAnyRole = (rolesToCheck: AppRole[]): boolean => {
    return roles.some(r => rolesToCheck.includes(r.role));
  };

  /**
   * Check if user has all of the specified roles
   */
  const hasAllRoles = (rolesToCheck: AppRole[]): boolean => {
    return rolesToCheck.every(roleToCheck => 
      roles.some(r => r.role === roleToCheck)
    );
  };

  /**
   * Get expiration date for a specific role
   */
  const getRoleExpiration = (role: AppRole): Date | null => {
    const userRole = roles.find(r => r.role === role);
    return userRole?.expires_at ? new Date(userRole.expires_at) : null;
  };

  /**
   * Check if a role is expiring soon (within 7 days)
   */
  const isRoleExpiringSoon = (role: AppRole): boolean => {
    const expiration = getRoleExpiration(role);
    if (!expiration) return false;
    
    const sevenDaysFromNow = new Date();
    sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
    
    return expiration <= sevenDaysFromNow;
  };

  // ============================================================================
  // CONVENIENCE CHECKS
  // ============================================================================

  const isAdmin = hasRole('admin');
  const isModerator = hasRole('moderator');
  const isPartner = hasRole('partner');
  const isPremium = hasRole('premium');
  const isStaff = hasAnyRole(['admin', 'moderator']);

  // ============================================================================
  // RETURN
  // ============================================================================

  return {
    // Data
    roles,
    loading,
    error,
    
    // Functions
    hasRole,
    hasAnyRole,
    hasAllRoles,
    getRoleExpiration,
    isRoleExpiringSoon,
    refetch,
    
    // Convenience booleans
    isAdmin,
    isModerator,
    isPartner,
    isPremium,
    isStaff,
  };
}

// ============================================================================
// ADMIN OPERATIONS HOOK (for role management)
// ============================================================================

export function useRoleManagement() {
  const { user } = useAuth();

  /**
   * Grant a role to a user (admin only)
   */
  const grantRole = async (
    targetUserId: string,
    role: AppRole,
    expiresAt?: Date
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const { error } = await supabase
        .from('user_roles')
        .insert({
          user_id: targetUserId,
          role,
          granted_by: user?.id,
          expires_at: expiresAt?.toISOString() || null,
        });

      if (error) {
        console.error('Error granting role:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error in grantRole:', error);
      return { success: false, error: 'Failed to grant role' };
    }
  };

  /**
   * Revoke a role from a user (admin only)
   */
  const revokeRole = async (
    targetUserId: string,
    role: AppRole
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const { error } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', targetUserId)
        .eq('role', role);

      if (error) {
        console.error('Error revoking role:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error in revokeRole:', error);
      return { success: false, error: 'Failed to revoke role' };
    }
  };

  /**
   * Update role expiration
   */
  const updateRoleExpiration = async (
    targetUserId: string,
    role: AppRole,
    expiresAt: Date | null
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const { error } = await supabase
        .from('user_roles')
        .update({ expires_at: expiresAt?.toISOString() || null })
        .eq('user_id', targetUserId)
        .eq('role', role);

      if (error) {
        console.error('Error updating role expiration:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error in updateRoleExpiration:', error);
      return { success: false, error: 'Failed to update role expiration' };
    }
  };

  /**
   * Get all users with a specific role (admin only)
   */
  const getUsersWithRole = async (role: AppRole): Promise<UserRole[]> => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('*, profiles(*)')
        .eq('role', role)
        .or('expires_at.is.null,expires_at.gt.now()');

      if (error) {
        console.error('Error fetching users with role:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getUsersWithRole:', error);
      return [];
    }
  };

  return {
    grantRole,
    revokeRole,
    updateRoleExpiration,
    getUsersWithRole,
  };
}

// ============================================================================
// LEGACY COMPATIBILITY HELPERS
// ============================================================================

/**
 * Hook for backwards compatibility with old profile-based role checks
 * 
 * @deprecated Use useUserRoles() instead
 */
export function useLegacyRoleCheck() {
  const { isAdmin, isPartner, isPremium } = useUserRoles();
  
  console.warn('useLegacyRoleCheck is deprecated. Update your component to use useUserRoles().');
  
  return {
    is_admin: isAdmin,
    is_partner: isPartner,
    is_premium: isPremium,
  };
}

// ============================================================================
// ROLE DISPLAY HELPERS
// ============================================================================

export function getRoleDisplayName(role: AppRole): string {
  const displayNames: Record<AppRole, string> = {
    admin: 'Administrator',
    moderator: 'Moderator',
    partner: 'Partner',
    premium: 'Premium',
    user: 'User',
  };
  return displayNames[role] || role;
}

export function getRoleColor(role: AppRole): string {
  const colors: Record<AppRole, string> = {
    admin: 'text-red-500',
    moderator: 'text-blue-500',
    partner: 'text-purple-500',
    premium: 'text-yellow-500',
    user: 'text-gray-500',
  };
  return colors[role] || 'text-gray-500';
}

export function getRoleBadgeColor(role: AppRole): string {
  const colors: Record<AppRole, string> = {
    admin: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
    moderator: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    partner: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
    premium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
    user: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
  };
  return colors[role] || 'bg-gray-100 text-gray-800';
}
